import React from 'react';
import { useTheme } from '../../ThemeContext';
import { useNavigate } from 'react-router-dom';

/**
 * Professional billing restriction popup component
 * Handles subscription access and units availability warnings
 *
 * @param {boolean} visible - Controls popup visibility
 * @param {string} type - Type of restriction: 'access', 'warning', or 'expired_units'
 * @param {string} message - Message to display
 * @param {Function} onClose - Function to close the popup
 * @param {boolean} showBackButton - Whether to show back button (for access restrictions)
 * @param {string} headline - Custom headline/title (optional)
 * @param {string} primaryButtonText - Custom primary button text (optional)
 * @param {string} secondaryButtonText - Custom secondary button text (optional)
 * @param {Function} onPrimaryAction - Custom primary button action (optional, defaults to back navigation or close)
 * @param {Function} onSecondaryAction - Custom secondary button action (optional, defaults to onClose)
 */
const BillingRestrictionPopup = ({
  visible,
  type = 'warning',
  message,
  onClose,
  showBackButton = false,
  headline,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryAction,
  onSecondaryAction
}) => {
  const { themeMode, currentTheme } = useTheme();
  const navigate = useNavigate();

  if (!visible) return null;

  const handleBackClick = () => {
    navigate(-1);
  };

  const isAccessRestriction = type === 'access';
  const isWarning = type === 'warning' || type === 'expired_units';

  // Theme-based styling
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  };

  const popupStyle = {
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    color: currentTheme.homePage.textColor,
    borderRadius: '16px',
    padding: '0',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: themeMode === 'dark' 
      ? '0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      : '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    border: themeMode === 'dark' ? '1px solid #302d4c' : '1px solid #e9e8ea',
    animation: 'slideIn 0.3s ease-out',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
  };

  const headerStyle = {
    padding: '24px 24px 0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
    paddingBottom: '16px',
    marginBottom: '24px'
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: isAccessRestriction ? '#e63e2b' : (isWarning ? '#ff8c00' : currentTheme.homePage.textColor),
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: currentTheme.homePage.textColor,
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px'
  };

  const contentStyle = {
    padding: '0 24px',
    marginBottom: '32px'
  };

  const messageStyle = {
    fontSize: '16px',
    lineHeight: '1.6',
    color: currentTheme.homePage.textColor,
    margin: '0',
    textAlign: 'left'
  };

  const footerStyle = {
    padding: '0 24px 24px 24px',
    display: 'flex',
    justifyContent: showBackButton ? 'space-between' : 'flex-end',
    gap: '12px'
  };

  const buttonBaseStyle = {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '80px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const primaryButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#e63e2b',
    color: '#ffffff'
  };

  const secondaryButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: themeMode === 'dark' ? '#302d4c' : '#e9e8ea',
    color: currentTheme.homePage.textColor
  };

  const getIcon = () => {
    if (isAccessRestriction) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM12 13c-.55 0-1-.45-1-1V8c0-.55.45-1 1-1s1 .45 1 1v4c0 .55-.45 1-1 1zm1 4h-2v-2h2v2z" fill="#e63e2b"/>
        </svg>
      );
    } else if (isWarning) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z" fill="#ff8c00"/>
        </svg>
      );
    }
    return null;
  };

  const getTitle = () => {
    if (headline) return headline;
    if (isAccessRestriction) return 'Access Restricted';
    if (isWarning) return 'Units Warning';
    return 'Notice';
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { 
              opacity: 0; 
              transform: translateY(-20px) scale(0.95); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0) scale(1); 
            }
          }
        `}
      </style>
      <div style={overlayStyle} onClick={onClose}>
        <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>
              {getIcon()}
              {getTitle()}
            </h2>
            <button 
              style={closeButtonStyle}
              onClick={onClose}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.7'}
            >
              ×
            </button>
          </div>
          
          <div style={contentStyle}>
            <p style={messageStyle}>{message}</p>
          </div>
          
          <div style={footerStyle}>
            {/* Secondary button (left side) */}
            {(showBackButton || secondaryButtonText) && (
              <button
                style={secondaryButtonStyle}
                onClick={onSecondaryAction || (showBackButton ? handleBackClick : onClose)}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = themeMode === 'dark' ? '#404040' : '#d0d0d0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = themeMode === 'dark' ? '#302d4c' : '#e9e8ea';
                }}
              >
                {secondaryButtonText || 'Go Back'}
              </button>
            )}
            {/* Primary button (right side) */}
            <button
              style={primaryButtonStyle}
              onClick={onPrimaryAction || onClose}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#e63e2b'}
            >
              {primaryButtonText || 'Stay Here'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BillingRestrictionPopup; 